import { Dispatch, Ref, SetStateAction, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/material';

import { useShallow } from 'zustand/react/shallow';

import { AdvertisingPackageResponse, SpaceResponse } from '@/api/data-contracts';
import { ReactComponent as NextIcon } from '@/assets/icons/next-icon.svg';
import AppButton from '@/shared/ui/AppButton';
import useGeneratikTableStore from '@/shared/ui/GeneratikTable/generatikTableStore';

import ShoppingCartList from './ShoppingCartList';
import ShoppingCartTotal from './ShoppingCartTotal';

interface Props {
  handleClose: () => void;
  setActiveStep: Dispatch<SetStateAction<number>>;
}

const ShoppingCartContentLists = forwardRef(function ShoppingCartContentLists(
  { handleClose, setActiveStep }: Props,
  ref: Ref<HTMLDivElement>,
) {
  const { t } = useTranslation();

  const { selectedSpaces, selectedPackages } = useGeneratikTableStore(
    useShallow((state) => ({
      selectedSpaces: (state.selectedRows['ADVERTISING_SPACES'] ?? []) as SpaceResponse[],
      selectedPackages: (state.selectedRows['ADVERTISING_PACKAGES'] ??
        []) as AdvertisingPackageResponse[],
    })),
  );

  return (
    <Stack
      ref={ref}
      sx={{
        padding: '0.75rem 1rem',
        gap: '0.75rem',
      }}
    >
      <Stack spacing={1.5} sx={{ overflowY: 'auto', maxHeight: '50vh' }}>
        <ShoppingCartList type="spaces" handleClose={handleClose} />
        <ShoppingCartList type="packages" handleClose={handleClose} />
      </Stack>

      <ShoppingCartTotal />

      <AppButton
        variant="primary"
        size="medium"
        fullWidth
        endIcon={<NextIcon />}
        disabled={!selectedSpaces.length && !selectedPackages.length}
        onClick={() => setActiveStep(1)}
        data-testid="shopping-cart-next-button"
      >
        {t('Next')}
      </AppButton>
    </Stack>
  );
});

export default ShoppingCartContentLists;
